/**
 * Diagram for the "How models think" post: one forward pass through the
 * layers (inner loop, solid) vs. the token being appended and the pass
 * repeating (outer loop, dashed). The last token's residual stream is drawn
 * as a whiteboard that accumulates notes as it descends the layers.
 */

const TOKENS: Array<{ label: string; x: number; w: number }> = [
  { label: "how", x: 40, w: 62 },
  { label: "do", x: 110, w: 50 },
  { label: "models", x: 168, w: 88 },
  { label: "think", x: 264, w: 70 },
  { label: "?", x: 342, w: 38 }
];

const LAYERS: Array<{ label: string; y: number }> = [
  { label: "Layer 1", y: 118 },
  { label: "Layer 2", y: 176 },
  { label: "Layer N−1", y: 272 },
  { label: "Layer N", y: 330 }
];

// Notes accumulating on the whiteboard as the pass deepens.
const NOTES: Array<{ label: string; y: number; opacity: number }> = [
  { label: "AI models, not fashion models", y: 196, opacity: 0.55 },
  { label: "a question, not a statement", y: 282, opacity: 0.75 },
  { label: "the next token takes shape", y: 368, opacity: 1 }
];

// Dots inside the residual bar: sparse at the top, dense at the bottom.
const DOTS: Array<{ cx: number; cy: number }> = [
  { cx: 208, cy: 140 },
  { cx: 196, cy: 188 },
  { cx: 218, cy: 210 },
  { cx: 202, cy: 248 },
  { cx: 216, cy: 268 },
  { cx: 194, cy: 292 },
  { cx: 210, cy: 312 },
  { cx: 198, cy: 336 },
  { cx: 220, cy: 348 },
  { cx: 206, cy: 368 },
  { cx: 194, cy: 384 },
  { cx: 216, cy: 392 }
];

const ReadWriteArrow = ({ y }: { y: number }) => (
  <g className="text-gray-500 dark:text-gray-400">
    <line x1={244} y1={y} x2={292} y2={y} stroke="currentColor" strokeWidth={1.5} />
    <polygon points={`244,${y} 252,${y - 4} 252,${y + 4}`} fill="currentColor" />
    <polygon points={`292,${y} 284,${y - 4} 284,${y + 4}`} fill="currentColor" />
  </g>
);

const ResidualStreamDiagram = () => (
  <figure className="my-8">
    <svg viewBox="-32 0 736 540" role="img" aria-label="Diagram of a forward pass: prompt tokens feed a residual stream that each layer reads and writes, producing one token that is appended before the pass repeats" className="w-full h-auto">
      {/* Prompt tokens */}
      <text x={40} y={26} fontSize={13} fontWeight={600} fill="currentColor" className="text-gray-700 dark:text-gray-300">
        Prompt (tokens)
      </text>
      {TOKENS.map(({ label, x, w }) => (
        <g key={label}>
          <rect x={x} y={38} width={w} height={34} rx={8} fill="currentColor" className="text-gray-100 dark:text-gray-800" />
          <rect x={x} y={38} width={w} height={34} rx={8} fill="none" stroke="currentColor" strokeWidth={1.25} className="text-gray-400 dark:text-gray-600" />
          <text x={x + w / 2} y={60} fontSize={14} textAnchor="middle" fill="currentColor" className="text-gray-800 dark:text-gray-200">
            {label}
          </text>
        </g>
      ))}

      {/* Prompt feeds the pass */}
      <g className="text-gray-500 dark:text-gray-400">
        <line x1={208} y1={72} x2={208} y2={102} stroke="currentColor" strokeWidth={1.5} />
        <polygon points="208,110 203,100 213,100" fill="currentColor" />
      </g>

      {/* Residual stream (the whiteboard) */}
      <rect x={180} y={112} width={56} height={296} rx={28} fill="currentColor" className="text-amber-100 dark:text-amber-950" />
      <rect x={180} y={112} width={56} height={296} rx={28} fill="none" stroke="currentColor" strokeWidth={1.5} className="text-amber-500 dark:text-amber-600" />
      {DOTS.map(({ cx, cy }, i) => (
        <circle key={i} cx={cx} cy={cy} r={3} fill="currentColor" className="text-amber-500 dark:text-amber-500" />
      ))}
      <text x={166} y={130} fontSize={12} fontWeight={600} textAnchor="end" fill="currentColor" className="text-amber-700 dark:text-amber-400">
        residual
      </text>
      <text x={166} y={146} fontSize={12} fontWeight={600} textAnchor="end" fill="currentColor" className="text-amber-700 dark:text-amber-400">
        stream
      </text>

      {/* Notes accumulating as the pass deepens */}
      {NOTES.map(({ label, y, opacity }) => (
        <text key={label} x={166} y={y} fontSize={12} fontStyle="italic" textAnchor="end" opacity={opacity} fill="currentColor" className="text-amber-800 dark:text-amber-300">
          {label}
        </text>
      ))}

      {/* Layers */}
      {LAYERS.map(({ label, y }) => (
        <g key={label}>
          <rect x={300} y={y} width={200} height={44} rx={10} fill="currentColor" className="text-indigo-50 dark:text-indigo-950" />
          <rect x={300} y={y} width={200} height={44} rx={10} fill="none" stroke="currentColor" strokeWidth={1.5} className="text-indigo-400 dark:text-indigo-600" />
          <text x={400} y={y + 27} fontSize={14} fontWeight={600} textAnchor="middle" fill="currentColor" className="text-indigo-700 dark:text-indigo-300">
            {label}
          </text>
          <ReadWriteArrow y={y + 22} />
        </g>
      ))}
      <text x={400} y={246} fontSize={18} textAnchor="middle" fill="currentColor" className="text-gray-500 dark:text-gray-400">
        &middot; &middot; &middot;
      </text>

      {/* Layer annotations */}
      <text x={512} y={136} fontSize={12} fill="currentColor" className="text-gray-600 dark:text-gray-400">
        reads the whiteboard,
      </text>
      <text x={512} y={152} fontSize={12} fill="currentColor" className="text-gray-600 dark:text-gray-400">
        writes notes back
      </text>
      <text x={512} y={348} fontSize={12} fill="currentColor" className="text-gray-600 dark:text-gray-400">
        still just numbers:
      </text>
      <text x={512} y={364} fontSize={12} fill="currentColor" className="text-gray-600 dark:text-gray-400">
        no token chosen yet
      </text>

      {/* The keyhole: rich vector squeezed into one token */}
      <g className="text-gray-500 dark:text-gray-400">
        <line x1={208} y1={408} x2={208} y2={438} stroke="currentColor" strokeWidth={1.5} />
        <polygon points="208,446 203,436 213,436" fill="currentColor" />
      </g>

      <rect x={138} y={446} width={140} height={40} rx={8} fill="currentColor" className="text-emerald-50 dark:text-emerald-950" />
      <rect x={138} y={446} width={140} height={40} rx={8} fill="none" stroke="currentColor" strokeWidth={1.5} className="text-emerald-500 dark:text-emerald-600" />
      <text x={208} y={471} fontSize={14} fontWeight={600} textAnchor="middle" fill="currentColor" className="text-emerald-700 dark:text-emerald-300">
        one token out
      </text>

      {/* Outer loop: token appended, pass repeats */}
      <g className="text-emerald-600 dark:text-emerald-400">
        <path d="M 278 466 L 600 466 L 600 55 L 402 55" fill="none" stroke="currentColor" strokeWidth={1.5} strokeDasharray="6 4" />
        <polygon points="394,55 404,50 404,60" fill="currentColor" />
        <text x={612} y={252} fontSize={12}>
          <tspan x={612} dy={0} fill="currentColor">appended,</tspan>
          <tspan x={612} dy={16} fill="currentColor">pass repeats</tspan>
        </text>
      </g>
    </svg>
    <figcaption className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
      Every token gets its own whiteboard; this is the last one's, and its notes are illustrative. The inner loop (solid): one trip through all the layers, entirely in numbers. The outer loop (dashed): the chosen token is appended to the text and the trip runs again for it, reusing the earlier tokens' boards.
    </figcaption>
  </figure>
);

export default ResidualStreamDiagram;
